import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import BaseScreen from 'components/base-screen';
import BottomSheet from 'components/bottom-sheet';
import Button, { ButtonProps } from 'components/button';
import BottomSheetResult from 'components/bottom-sheet-result';
import ApartmentListItem from 'components/apartment-list-item';

import useBoundStore from 'store';
import { uploadDocuments } from 'api';
import { BuyApartmentFlowPaths, SellApartmentFlowPaths } from 'routes';
import useApartmentSaleInfo, { APIApartmentSellInfoRes } from 'api/hooks/use-apartment-sale-info';

import styles from './styles.module.scss';
import iconUpload from '../../assets/images/icon-file-upload.png';
import clsx from 'clsx';
import iconDelete from '../../assets/images/icon-delete-cross.svg';
import iconFile from '../../assets/images/icon-files.svg';
import { useDropzone } from 'react-dropzone';

export type InputApartmentPriceNavigateProps = {
  apartment: APIApartmentSellInfoRes & { id: number; price: number };
};

const DocumentsNotificationMode: React.FC = () => {
  const navigate = useNavigate();

  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);
  const sellApartmentNumber = useBoundStore((s) => s.sellApartmentNumber);
  const navigationProps = useLocation().state as InputApartmentPriceNavigateProps | undefined;

  const saleInfo = useApartmentSaleInfo(
    selectedSellBuilding.id,
    navigationProps ? '' : sellApartmentNumber,
  );

  const apartmentSellInfo = navigationProps?.apartment ?? saleInfo.apartmentSellInfo;
  const [priceWithoutMarketPrise, setPriceWithoutMarketPrise] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [files, setFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // @ts-ignore
      setFiles([...files, ...acceptedFiles]);
    },
    [files, setFiles],
  );

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDrop,
  });

  const handleRemoveFileInput = (index: any) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const buttons: ButtonProps[] = [
    {
      label: 'Отправить',
      disabled: !files.length,
      loading: isSubmitting,
      onClick: async () => {
        const formData = new FormData();

        files.forEach((file, id) => {
          formData.append(`files`, file);
        });

        setIsSubmitting(true);
        const uploadDocumentsRes = await uploadDocuments(
          // @ts-ignore
          navigationProps ? navigationProps?.apartment?.id : null,
          formData,
        );
        setIsSubmitting(false);

        if (uploadDocumentsRes.result) {
          navigate(SellApartmentFlowPaths.sellFinishScreen);
          useBoundStore.getState().showToast({
            message: 'Документы добавлены успешно',
          });
        } else {
          useBoundStore.getState().showToast({
            message: 'Ошибка добавления документа',
          });
        }
      },
    },
    {
      label: 'Отмена',
      variant: 'transparent',
      onClick: () => {
        navigate(-1);
      },
    },
  ];

  const handleDismissBottomSheet = () => {
    setIsBottomSheetOpen(false);
    navigate(BuyApartmentFlowPaths.main);
  };

  return (
    <BaseScreen
      padContent
      title='Продать объект'
      buttons={buttons}
      onBackButtonClick={() => navigate(-1)}
    >
      <div className={styles.container}>
        <ApartmentListItem
          isFavorite={false}
          price={navigationProps?.apartment?.price}
          squarePrice={
            apartmentSellInfo?.areaInSqM && priceWithoutMarketPrise
              ? ~~(priceWithoutMarketPrise / apartmentSellInfo.areaInSqM)
              : undefined
          }
          image={apartmentSellInfo?.planImage ?? ''}
          rooms={apartmentSellInfo?.roomCount ?? 0}
          area={apartmentSellInfo?.areaInSqM ?? 0}
          housingEstate={selectedSellComplex.title}
          floor={
            apartmentSellInfo
              ? { floor: apartmentSellInfo.floor, total: apartmentSellInfo.floorCount }
              : { floor: 0, total: 0 }
          }
        />
      </div>
      <h4 className={styles.title}>Загрузите документы подтверждающие собственность</h4>

      <div {...getRootProps()} className={clsx(styles.labelFileUpload, false && styles.dragActive)}>
        <input {...getInputProps()} />
        <div className={styles.uploadContent}>
          <img
            src={iconUpload}
            alt='Перетащите файл в область загрузки или выберите на компьютере'
          />
          <div className={styles.uploadButton}>
            Загрузите документы подтверждающие собственность, фотографии квартиры, планировку
          </div>
          <p>{'PDF, DOC, PNG, TIFF, JPG'}</p>
        </div>
      </div>

      <div className={styles.fileList}>
        {files.map((file: any, index) => {
          return (
            <div className={styles.uploadDocument} key={index}>
              <div>
                <img src={iconFile} />
              </div>
              <p className={styles.titleDocument}>{file?.name}</p>
              <button type='button' onClick={() => handleRemoveFileInput(index)}>
                <img src={iconDelete} alt='Удалить' />
              </button>
            </div>
          );
        })}
      </div>

      <BottomSheet open={isBottomSheetOpen} onDismiss={handleDismissBottomSheet}>
        <BottomSheetResult type='info' header='Заявка принята'>
          Ваша заявка отправлена на модерацию
        </BottomSheetResult>
        <Button label='Отлично' onClick={handleDismissBottomSheet} />
      </BottomSheet>
    </BaseScreen>
  );
};

export default DocumentsNotificationMode;
